import React, { useCallback, useEffect, useState } from 'react';
import { backofficeAuth } from '../backofficeFirebaseClient';
import { Search, Filter, X, RefreshCw } from 'lucide-react';

const STALE_MS = 5 * 60 * 1000;

const CompanyDirectory = () => {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all'); // all, name, id
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [lastSyncSource, setLastSyncSource] = useState('listener');
  const [isStale, setIsStale] = useState(false);

  const loadCompanies = useCallback(async (source = 'manual_refresh') => {
    try {
      setLoading(true);
      setError('');
      const token = await backofficeAuth.currentUser?.getIdToken?.();
      const resp = await fetch('https://us-central1-acctoo-backoffice.cloudfunctions.net/listCompanies', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await resp.json();
      setCompanies(json?.companies || []);
      setFilteredCompanies(json?.companies || []);
      setLastSyncedAt(new Date().toISOString());
      setLastSyncSource(source);
      setIsStale(false);
    } catch (e) {
      setError(`Firebase: ${e.code || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies('listener');
  }, [loadCompanies]);

  useEffect(() => {
    if (!lastSyncedAt) return undefined;
    const tick = () => {
      const age = Date.now() - new Date(lastSyncedAt).getTime();
      setIsStale(age > STALE_MS);
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [lastSyncedAt]);

  // Filter companies based on search term and filter type
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCompanies(companies);
      return;
    }

    const filtered = companies.filter(company => {
      const searchLower = searchTerm.toLowerCase();
      
      switch (filterBy) {
        case 'name':
          return company.name?.toLowerCase().includes(searchLower);
        case 'id':
          return company.id?.toLowerCase().includes(searchLower);
        case 'all':
        default:
          return (
            company.name?.toLowerCase().includes(searchLower) ||
            company.id?.toLowerCase().includes(searchLower)
          );
      }
    });

    setFilteredCompanies(filtered);
  }, [searchTerm, filterBy, companies]);

  const clearSearch = () => {
    setSearchTerm('');
    setFilterBy('all');
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-xl font-semibold">Companies ({filteredCompanies.length})</h3>
            {lastSyncedAt && (
              <p className={`text-xs mt-1 ${isStale ? 'text-amber-700' : 'text-gray-500'}`}>
                Last synced: {lastSyncedAt} · source: {lastSyncSource}
                {isStale ? ' · list may be stale — refresh recommended' : ''}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button
              type="button"
              onClick={() => loadCompanies('manual_refresh')}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
        
        {/* Search and Filter Controls */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Filter Dropdown */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Fields</option>
                <option value="name">Company Name</option>
                <option value="id">Company ID</option>
              </select>
            </div>
          </div>
          
          {/* Search Results Info */}
          {searchTerm && (
            <div className="text-sm text-gray-600">
              Showing {filteredCompanies.length} of {companies.length} companies
              {filteredCompanies.length === 0 && searchTerm && (
                <span className="text-red-600 ml-2">No companies found matching your search</span>
              )}
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3">Company ID</th>
                <th className="text-left py-2 px-3">Name</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((c) => (
                <tr key={c.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-3 font-mono">{c.id}</td>
                  <td className="py-2 px-3">{c.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <div className="text-sm text-gray-500 mt-3">Loading...</div>}
        </div>
      </div>
    </div>
  );
};

export default CompanyDirectory;


